import { parseFeeModels, getFeeModels as resolveFeeModels } from './feeModelUtils';
import {
  isCreatorPayoutPaid,
  formatPaymentAmountCents,
  getCompletedPayoutDisplay,
} from './collaborationPayoutDisplay';

export {
  isCreatorPayoutPaid,
  formatPaymentAmountCents,
  getCompletedPayoutDisplay,
};

/**
 * Brand journey: payment step is satisfied when payout is recorded or the campaign is closed.
 */
export const isPaymentStepComplete = (collaboration) => {
  if (!collaboration) return false;
  return (
    !!collaboration.payment_released
    || !!collaboration.paymentReleased
    || !!collaboration.payment_complete
    || !!collaboration.paymentComplete
    || !!collaboration.creator_paid_at
    || !!collaboration.creatorPaidAt
    || collaboration.status === 'completed'
    || !!collaboration.campaign_complete
  );
};

/**
 * Accepted / journey compensation (invite + application shapes on collaboration object).
 */
export const getFinalAcceptedFeeModels = (collaboration) => {
  if (!collaboration) return null;

  const normalizedFromFeeModelsPayload = (payload) => {
    if (!parseFeeModels(payload).length) return null;
    const got = resolveFeeModels(collaboration, payload);
    return got.length ? got : null;
  };

  const sender = collaboration.last_negotiation_sender_type;

  if (sender === 'brand') {
    const out = normalizedFromFeeModelsPayload(collaboration.brand_counter_offer_fee_models)
      || normalizedFromFeeModelsPayload(collaboration.brand_latest_proposed_fee_models)
      || normalizedFromFeeModelsPayload(collaboration.negotiated_fee_models);
    if (out?.length) return out;
  } else if (sender === 'influencer') {
    const out = normalizedFromFeeModelsPayload(collaboration.negotiated_fee_models)
      || normalizedFromFeeModelsPayload(collaboration.influencer_latest_proposed_fee_models);
    if (out?.length) return out;
  }

  // Deal-level rates only — never fall back to live campaign fee_models
  const generic = normalizedFromFeeModelsPayload(collaboration.negotiated_fee_models)
    || normalizedFromFeeModelsPayload(collaboration.brand_counter_offer_fee_models)
    || normalizedFromFeeModelsPayload(collaboration.brand_latest_proposed_fee_models)
    || normalizedFromFeeModelsPayload(collaboration.proposed_fee_models);
  if (generic?.length) return generic;

  return null;
};

export const getFinalAcceptedRate = (collaboration) => {
  const feeModels = getFinalAcceptedFeeModels(collaboration);
  if (feeModels && feeModels.length > 0) {
    const r = feeModels[0]?.rate;
    return r != null && r !== '' ? r : null;
  }

  if (collaboration?.brand_counter_offer != null && collaboration?.brand_counter_offer !== '') {
    return collaboration.brand_counter_offer;
  }
  if (collaboration?.negotiated_rate != null && collaboration?.negotiated_rate !== '') {
    return collaboration.negotiated_rate;
  }
  const payloads = [
    collaboration?.brand_latest_proposed_fee_models,
    collaboration?.negotiated_fee_models,
    collaboration?.influencer_latest_proposed_fee_models,
  ];
  for (let i = 0; i < payloads.length; i += 1) {
    const parsed = parseFeeModels(payloads[i]);
    if (parsed.length) return parsed[0].rate;
  }
  return collaboration?.starting_rate ?? collaboration?.campaign_price ?? null;
};

export const getFinalAcceptedFeeModelType = (collaboration) => {
  const feeModels = getFinalAcceptedFeeModels(collaboration);
  if (feeModels && feeModels.length > 0) {
    return feeModels[0]?.type || null;
  }
  return null;
};

const formatUsdAmount = (rate) => {
  const amount = Number.parseFloat(rate);
  if (!Number.isFinite(amount)) return null;
  const cents = Math.round(amount * 100);
  const formatted = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
  return `$${formatted}`;
};

export const formatCampaignProductPrice = (collaboration) => {
  const raw = collaboration?.campaign_price ?? collaboration?.price;
  const amount = Number.parseFloat(raw);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return formatUsdAmount(amount);
};

export const getEstimatedLiveSalesFromProof = (collaboration) => {
  const units = Number(collaboration?.deliverable?.products_sold);
  const unitPrice = Number.parseFloat(
    collaboration?.campaign_price ?? collaboration?.price,
  );
  if (!Number.isFinite(units) || units < 0) return null;
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) return null;
  const amount = Math.round(units * unitPrice * 100) / 100;
  if (amount <= 0) return null;
  return {
    units,
    unitPrice,
    amount,
    formatted: formatUsdAmount(amount),
    unitPriceFormatted: formatUsdAmount(unitPrice),
  };
};

const formatSingleFeeModel = (feeModel) => {
  if (!feeModel || feeModel.rate === undefined || feeModel.rate === null) return null;

  const { type, rate } = feeModel;
  const usd = formatUsdAmount(rate);

  if (type === 'flat_rate') return usd ? `${usd} USD` : null;
  if (type === 'commission' || type === 'commission_rate') return `${rate}% Commission`;
  if (type === 'hourly_rate') return usd ? `${usd}/hr` : null;
  return usd;
};

const COMPENSATION_ROW_LABELS = {
  flat_rate: 'Flat fee',
  flat: 'Flat fee',
  hourly_rate: 'Hourly',
  hourly: 'Hourly',
  commission: 'Commission',
  commission_rate: 'Commission',
};

export const JOURNEY_PAYOUT_COPY = {
  paymentDue: 'After the brand approves content',
  payout: 'Via Stripe once the brand releases payment',
  milletFee: '5% (paid by the brand)',
};

export const getCompensationTermRows = (feeModels, fallbackText = null) => {
  const rows = (feeModels || []).map((feeModel) => {
    if (!feeModel || feeModel.rate === undefined || feeModel.rate === null) return null;
    const usd = formatUsdAmount(feeModel.rate);
    const label = COMPENSATION_ROW_LABELS[feeModel.type];
    if (label === 'Flat fee' && usd) return { label, value: usd };
    if (label === 'Hourly' && usd) return { label, value: `${usd}/hr` };
    if (label === 'Commission') return { label, value: `${feeModel.rate}%` };
    if (usd) return { label: 'Compensation', value: usd };
    return null;
  }).filter(Boolean);

  if (rows.length > 0) return rows;
  if (fallbackText) return [{ label: 'Compensation', value: fallbackText }];
  return [];
};

export const getFlatRateFeeModel = (feeModels) => (
  feeModels?.find((fm) => fm.type === 'flat_rate' || fm.type === 'flat') || null
);

export const getHourlyFeeModel = (feeModels) => (
  feeModels?.find((fm) => fm.type === 'hourly_rate' || fm.type === 'hourly') || null
);

export const getAgreedHoursWorked = (feeModels) => {
  const hourlyRate = getHourlyFeeModel(feeModels);
  if (!hourlyRate) return null;
  const hours = Number.parseFloat(hourlyRate.hours);
  return Number.isFinite(hours) && hours > 0 ? hours : null;
};

/**
 * Recover hours from a stored checkout total (e.g. pending Stripe amount) so the
 * PAID step can keep showing the hourly line after refresh / Stripe return.
 */
export const deriveHoursWorkedFromPaymentAmount = (feeModels, amountCents, { commissionCents = 0 } = {}) => {
  const hourlyRate = getHourlyFeeModel(feeModels);
  const totalCents = Number.parseInt(amountCents, 10);
  if (!hourlyRate || !Number.isFinite(totalCents) || totalCents <= 0) return null;

  const rate = Number.parseFloat(hourlyRate.rate);
  if (!Number.isFinite(rate) || rate <= 0) return null;

  const flatRate = getFlatRateFeeModel(feeModels);
  const flatCents = flatRate?.rate != null && flatRate.rate !== ''
    ? Math.round(Number.parseFloat(flatRate.rate) * 100)
    : 0;
  const settledCommissionCents = Number.isFinite(commissionCents) && commissionCents > 0
    ? commissionCents
    : 0;
  const hourlyCents = totalCents - flatCents - settledCommissionCents;
  if (!Number.isFinite(hourlyCents) || hourlyCents <= 0) return null;

  const hours = Math.round((hourlyCents / (rate * 100)) * 100) / 100;
  if (!(Number.isFinite(hours) && hours > 0)) return null;
  if (Math.round(hours * rate * 100) !== hourlyCents) return null;
  return hours;
};

export const getCommissionFeeModel = (feeModels) => (
  feeModels?.find((fm) => fm.type === 'commission_rate' || fm.type === 'commission') || null
);

export const calculateCommissionCentsFromGmv = (feeModels, gmvDollars) => {
  const commissionModel = getCommissionFeeModel(feeModels);
  if (!commissionModel) return 0;
  const gmv = Number.parseFloat(gmvDollars);
  const rate = Number.parseFloat(commissionModel.rate);
  if (!Number.isFinite(gmv) || gmv <= 0 || !Number.isFinite(rate) || rate <= 0) return 0;
  return Math.round(gmv * rate);
};

/**
 * Recover GMV from a stored checkout total when flat/hourly are known.
 */
export const deriveGmvDollarsFromPaymentAmount = (feeModels, amountCents, { hoursWorked } = {}) => {
  const commissionModel = getCommissionFeeModel(feeModels);
  const totalCents = Number.parseInt(amountCents, 10);
  if (!commissionModel || !Number.isFinite(totalCents) || totalCents <= 0) return null;

  const rate = Number.parseFloat(commissionModel.rate);
  if (!Number.isFinite(rate) || rate <= 0) return null;

  const flatRate = getFlatRateFeeModel(feeModels);
  const flatCents = flatRate?.rate != null && flatRate.rate !== ''
    ? Math.round(Number.parseFloat(flatRate.rate) * 100)
    : 0;

  let hourlyCents = 0;
  const hourlyRate = getHourlyFeeModel(feeModels);
  if (hourlyRate) {
    const hours = Number.parseFloat(hoursWorked ?? hourlyRate.hours);
    if (!Number.isFinite(hours) || hours <= 0) return null;
    hourlyCents = Math.round(hours * Number.parseFloat(hourlyRate.rate) * 100);
  }

  const commissionCents = totalCents - flatCents - hourlyCents;
  if (!Number.isFinite(commissionCents) || commissionCents <= 0) return null;

  const gmvDollars = Math.round((commissionCents / rate) * 100) / 100;
  if (!(Number.isFinite(gmvDollars) && gmvDollars > 0)) return null;
  if (Math.round(gmvDollars * rate) !== commissionCents) return null;
  return gmvDollars;
};

/**
 * True when an agreed commission rate remains unsettled after creator payout.
 * Commission settles later from GMV; `commission_paid_at` clears this flag.
 */
export const hasOutstandingCampaignCommission = (collaboration) => {
  if (!collaboration || collaboration.commission_paid_at || collaboration.commissionPaidAt) {
    return false;
  }
  if (!isCreatorPayoutPaid(collaboration)) return false;
  const feeModels = getFinalAcceptedFeeModels(collaboration) || [];
  const commission = getCommissionFeeModel(feeModels);
  const rate = Number.parseFloat(commission?.rate);
  return Number.isFinite(rate) && rate > 0;
};

/**
 * Copy for brand/influencer notices when campaign commission is still outstanding.
 * @param {'brand'|'influencer'} role
 * @param {number|string} rate
 * @param {{ paymentAmountLabel?: string|null }} [options]
 */
export const getOutstandingCommissionCopy = (role, rate, { paymentAmountLabel = null } = {}) => {
  const rateLabel = `${rate}%`;
  if (role === 'brand') {
    return {
      title: 'Commission still due',
      body: `Creator payout is complete. ${rateLabel} campaign commission settles after live sales/GMV are known — it was not included in the payment just completed.`,
    };
  }
  let body = `${rateLabel} campaign commission is paid separately after GMV is known — it was not included in this payout.`;
  if (paymentAmountLabel) {
    body = `Your ${paymentAmountLabel} creator payout is received. ${body}`;
  }
  return {
    title: 'Commission still pending',
    body,
  };
};

export const buildCampaignPaymentBreakdown = ({
  feeModels,
  hoursWorked,
  gmvDollars,
  commissionCents = 0,
  includeCommissionInBreakdown = true,
}) => {
  if (!feeModels?.length) {
    return {
      lines: [],
      dueNowCents: 0,
      commissionCents: 0,
      totalCents: 0,
      hasCommission: false,
      isCheckoutReady: false,
    };
  }

  const lines = [];
  let dueNowCents = 0;
  let requiresHours = false;
  let requiresGmv = false;

  const flatRate = getFlatRateFeeModel(feeModels);
  if (flatRate?.rate != null && flatRate.rate !== '') {
    const amountCents = Math.round(Number.parseFloat(flatRate.rate) * 100);
    lines.push({
      key: 'flat_fee',
      label: 'Flat fee',
      amountCents,
    });
    dueNowCents += amountCents;
  }

  const hourlyRate = getHourlyFeeModel(feeModels);
  if (hourlyRate) {
    const hours = Number.parseFloat(hoursWorked ?? hourlyRate.hours);
    if (Number.isFinite(hours) && hours > 0) {
      const amountCents = Math.round(hours * Number.parseFloat(hourlyRate.rate) * 100);
      lines.push({
        key: 'hourly',
        label: `Hourly (${hours} hrs × $${hourlyRate.rate}/hr)`,
        amountCents,
      });
      dueNowCents += amountCents;
    } else {
      requiresHours = true;
      lines.push({
        key: 'hourly',
        label: `Hourly ($${hourlyRate.rate}/hr)`,
        amountCents: null,
      });
    }
  }

  const commissionModel = getCommissionFeeModel(feeModels);
  const hasCommission = !!commissionModel || commissionCents > 0;
  let normalizedCommissionCents = Number.isFinite(commissionCents) && commissionCents > 0
    ? commissionCents
    : 0;
  if (normalizedCommissionCents <= 0 && includeCommissionInBreakdown && commissionModel) {
    normalizedCommissionCents = calculateCommissionCentsFromGmv(feeModels, gmvDollars);
  }

  if (includeCommissionInBreakdown && hasCommission) {
    const rateLabel = commissionModel?.rate != null ? `${commissionModel.rate}%` : null;
    if (normalizedCommissionCents > 0) {
      const gmv = Number.parseFloat(gmvDollars);
      let commissionLabel = rateLabel ? `Commission (${rateLabel})` : 'Commission';
      if (Number.isFinite(gmv) && gmv > 0 && rateLabel) {
        const gmvDisplay = Number.isInteger(gmv) ? String(gmv) : gmv.toFixed(2);
        commissionLabel = `Commission (${rateLabel} of $${gmvDisplay})`;
      }
      lines.push({
        key: 'commission',
        label: commissionLabel,
        amountCents: normalizedCommissionCents,
      });
      dueNowCents += normalizedCommissionCents;
    } else {
      requiresGmv = true;
      lines.push({
        key: 'commission',
        label: rateLabel ? `Commission (${rateLabel})` : 'Commission',
        amountCents: null,
      });
    }
  }

  return {
    lines,
    dueNowCents,
    commissionCents: normalizedCommissionCents,
    totalCents: dueNowCents,
    hasCommission,
    isCheckoutReady: dueNowCents > 0 && !requiresHours && !requiresGmv,
  };
};

const parsePositiveCheckoutInput = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getSettledCampaignPaymentBreakdown = (collaboration) => {
  const feeModels = getFinalAcceptedFeeModels(collaboration);
  if (!feeModels?.length) return null;

  const storedHours = parsePositiveCheckoutInput(collaboration?.payment_hours_worked);
  const storedGmv = parsePositiveCheckoutInput(collaboration?.payment_gmv_dollars);
  const hoursWorked = storedHours
    ?? parsePositiveCheckoutInput(collaboration?.deliverable?.hours_worked);
  if (storedHours == null && storedGmv == null && hoursWorked == null) {
    return null;
  }

  return {
    ...buildCampaignPaymentBreakdown({
      feeModels,
      hoursWorked,
      gmvDollars: storedGmv,
    }),
    hoursWorked,
    gmvDollars: storedGmv,
  };
};

export const getSettledCampaignPaymentDisplayRows = (collaboration) => {
  const settled = getSettledCampaignPaymentBreakdown(collaboration);
  if (!settled) return [];

  const currency = collaboration?.payment_currency;
  const rows = [];
  if (settled.hoursWorked != null) {
    rows.push({
      key: 'hours',
      label: 'Hours confirmed',
      value: String(settled.hoursWorked),
    });
  }
  if (settled.gmvDollars != null) {
    const gmvLabel = formatPaymentAmountCents(
      Math.round(Number(settled.gmvDollars) * 100),
      currency,
    );
    if (gmvLabel) {
      rows.push({
        key: 'gmv',
        label: 'Live sales / GMV',
        value: gmvLabel,
      });
    }
  }
  settled.lines.forEach((line) => {
    if (line.amountCents == null) return;
    const amount = formatPaymentAmountCents(line.amountCents, currency);
    if (!amount) return;
    rows.push({
      key: line.key,
      label: line.label,
      value: amount,
    });
  });
  return rows;
};

export const calculateFlatFeePaymentCents = (feeModels, hoursWorked) => {
  const breakdown = buildCampaignPaymentBreakdown({ feeModels, hoursWorked, commissionCents: 0 });
  return breakdown.isCheckoutReady ? breakdown.dueNowCents : null;
};

export const formatCompensationRate = (rate, feeModel, feeModels = null) => {
  if (feeModels && Array.isArray(feeModels) && feeModels.length > 0) {
    const formatted = feeModels
      .map((fm) => formatSingleFeeModel(fm))
      .filter((fm) => fm !== null);

    if (formatted.length === 0) return null;
    if (formatted.length === 1) return formatted[0];
    if (formatted.length === 2) return `${formatted[0]} and ${formatted[1]}`;
    return `${formatted.slice(0, -1).join(', ')}, and ${formatted[formatted.length - 1]}`;
  }

  if (!rate) return null;
  if (feeModel === 'flat_rate') return `$${rate} USD`;
  if (feeModel === 'commission' || feeModel === 'commission_rate') return `${rate}%`;
  if (feeModel === 'hourly_rate') return `$${rate}/hr`;
  return `$${rate}`;
};

export const getDigitalCurrentStep = (collaboration, { includeAccepted = true } = {}) => {
  if (!collaboration) return 0;
  if (collaboration.status === 'pending') return 0;
  const active = collaboration.status === 'active'
    || (includeAccepted && collaboration.status === 'accepted');
  if (active) {
    if (!collaboration.digital_access_granted_at) return 1;
    if (!collaboration.digital_access_confirmed_at) return 2;
    if (!collaboration.contract_url || collaboration.contract_status !== 'signed') return 3;
    if (!collaboration.contract_verified_by_brand) return 4;
    const ds = collaboration.deliverable?.status;
    if (!ds || ds === 'pending' || ds === 'rejected' || ds === 'scheduled') return 5;
    if (ds === 'submitted' || ds === 'completed') return 6;
    if (ds === 'approved') {
      if (!isPaymentStepComplete(collaboration)) return 7;
      return 8;
    }
    return 5;
  }
  if (collaboration.status === 'completed') return 9;
  return 0;
};

export const getDigitalCompletedStepsCount = (collaboration, collaborationHasPostContent) => {
  if (!collaboration) return 0;
  let c = 0;
  const active = collaboration.status === 'active'
    || collaboration.status === 'accepted'
    || collaboration.status === 'completed';
  if (active) c += 1;
  if (collaboration.digital_access_granted_at) c += 1;
  if (collaboration.digital_access_confirmed_at) c += 1;
  if (collaboration.contract_url) c += 1;
  if (collaboration.contract_verified_by_brand) c += 1;
  const ds = collaboration.deliverable?.status;
  if (ds === 'submitted' || ds === 'completed' || ds === 'approved') c += 1;
  if (ds === 'approved' || collaborationHasPostContent(collaboration)) c += 1;
  if (isPaymentStepComplete(collaboration)) c += 1;
  if (collaboration.status === 'completed' || collaboration.campaign_complete) c += 1;
  return c;
};
